import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { motion } from 'framer-motion';
import Column from './Column';
import AddTaskModal from './AddTaskModal';
import { saveData } from '../utils/localStorage';
import { BoardData, Task } from '../types';
import toast from 'react-hot-toast';

interface BoardProps {
  boardData: BoardData;
  updateBoardData: (data: BoardData) => void;
  filteredTasks: Task[];
}

const Board: React.FC<BoardProps> = ({ boardData, updateBoardData, filteredTasks }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const handleDragEnd = (result: DropResult): void => {
    const { destination, source, draggableId, type } = result;

    if (!destination) {
      toast('Task returned to original position', {
        icon: '📍',
        duration: 2000
      });
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const start = boardData.boards[boardData.currentBoardId].columns[source.droppableId];
    const finish = boardData.boards[boardData.currentBoardId].columns[destination.droppableId];

    if (type === 'column') {
      const newColumnOrder = Array.from(boardData.boards[boardData.currentBoardId].columnOrder);
      const [removed] = newColumnOrder.splice(source.index, 1);
      newColumnOrder.splice(destination.index, 0, removed);

      const newBoardData: BoardData = {
        ...boardData,
        boards: {
          ...boardData.boards,
          [boardData.currentBoardId]: {
            ...boardData.boards[boardData.currentBoardId],
            columnOrder: newColumnOrder,
          }
        }
      };

      updateBoardData(newBoardData);
      saveData(newBoardData);
      toast.success('Column reordered successfully!');
      return;
    }

    if (start === finish) {
      const newTaskIds = Array.from(start.taskIds);
      const [removed] = newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, removed);

      const newColumn = {
        ...start,
        taskIds: newTaskIds,
      };

      const newBoardData: BoardData = {
        ...boardData,
        boards: {
          ...boardData.boards,
          [boardData.currentBoardId]: {
            ...boardData.boards[boardData.currentBoardId],
            columns: {
              ...boardData.boards[boardData.currentBoardId].columns,
              [newColumn.id]: newColumn,
            },
          }
        }
      };

      updateBoardData(newBoardData);
      saveData(newBoardData);
      toast.success('Task reordered within column!');
      return;
    }

    // Moving from one column to another
    const startTaskIds = Array.from(start.taskIds);
    const [removed] = startTaskIds.splice(source.index, 1);
    const newStart = {
      ...start,
      taskIds: startTaskIds,
    };

    const finishTaskIds = Array.from(finish.taskIds);
    finishTaskIds.splice(destination.index, 0, removed);
    const newFinish = {
      ...finish,
      taskIds: finishTaskIds,
    };

    const newBoardData: BoardData = {
      ...boardData,
      boards: {
        ...boardData.boards,
        [boardData.currentBoardId]: {
          ...boardData.boards[boardData.currentBoardId],
          columns: {
            ...boardData.boards[boardData.currentBoardId].columns,
            [newStart.id]: newStart,
            [newFinish.id]: newFinish,
          },
        }
      }
    };

    updateBoardData(newBoardData);
    saveData(newBoardData);

    const sourceColumnName = start.title;
    const destColumnName = finish.title;
    toast.success(`Task moved: ${sourceColumnName} → ${destColumnName}`, {
      duration: 3000,
      icon: '✅'
    });
  };

  const handleAddTask = (columnId: string): void => {
    setEditingTask(null);
    setIsModalOpen(true);
  };

  const handleEditTask = (taskId: string): void => {
    const task = boardData.boards[boardData.currentBoardId].tasks[taskId];
    setEditingTask(task);
    setIsModalOpen(true);
  };

  const handleDeleteTask = (taskId: string): void => {
    const task = boardData.boards[boardData.currentBoardId].tasks[taskId];

    toast.error(
      <div className='flex flex-col'>
        <span>Delete "{task.title}"?</span>
        <div className='flex space-x-2 mt-2'>
          <button
            className='px-3 py-1 bg-red-600 text-white rounded text-sm'
            onClick={() => {
              const newTasks = { ...boardData.boards[boardData.currentBoardId].tasks };
              delete newTasks[taskId];

              const newColumns = { ...boardData.boards[boardData.currentBoardId].columns };
              Object.keys(newColumns).forEach(columnId => {
                newColumns[columnId] = {
                  ...newColumns[columnId],
                  taskIds: newColumns[columnId].taskIds.filter(id => id !== taskId),
                };
              });

              const newBoardData: BoardData = {
                ...boardData,
                boards: {
                  ...boardData.boards,
                  [boardData.currentBoardId]: {
                    ...boardData.boards[boardData.currentBoardId],
                    tasks: newTasks,
                    columns: newColumns,
                  }
                }
              };

              updateBoardData(newBoardData);
              saveData(newBoardData);
              toast.success('Task deleted successfully!');
            }}
          >
            Delete
          </button>
          <button
            className='px-3 py-1 bg-gray-300 text-black rounded text-sm'
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        duration: 5000,
        style: { background: '#fee2e2', color: '#dc2626' }
      }
    );
  };

  const handleSaveTask = (taskData: Partial<Task>): void => {
    let newBoardData: BoardData = { ...boardData };

    if (editingTask) {
      // Update existing task
      newBoardData.boards[boardData.currentBoardId].tasks[editingTask.id] = { ...editingTask, ...taskData } as Task;
      toast.success('Task updated successfully!', { icon: '✏️' });
    } else {
      // Add new task
      const columnId = (taskData as any).columnId || 'todo';
      const newTask: Task = {
        id: `task-${Date.now()}`,
        title: taskData.title || '',
        content: taskData.content || '',
        priority: taskData.priority || 'medium',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        subtasks: [],
        tags: [],
        completed: false,
        assigneeIds: [],
        comments: [],
        attachments: []
      };

      newBoardData.boards[boardData.currentBoardId].tasks[newTask.id] = newTask;
      newBoardData.boards[boardData.currentBoardId].columns[columnId].taskIds.push(newTask.id);
      toast.success('Task added successfully!', { icon: '🎉' });
    }

    updateBoardData(newBoardData);
    saveData(newBoardData);
    setIsModalOpen(false);
    setEditingTask(null);
  };

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    setEditingTask(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex space-x-6 overflow-x-auto pb-4">
          {boardData.boards[boardData.currentBoardId].columnOrder.map((columnId, index) => {
            const column = boardData.boards[boardData.currentBoardId].columns[columnId];
            const tasks = column.taskIds.map(taskId => boardData.boards[boardData.currentBoardId].tasks[taskId]);

            // Filter tasks if there's a search term
            const filteredColumnTasks = filteredTasks.length > 0
              ? tasks.filter(task => filteredTasks.includes(task))
              : tasks;

            return (
              <Column
                key={column.id}
                column={column}
                tasks={filteredColumnTasks}
                index={index}
                onAddTask={() => handleAddTask(column.id)}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
              />
            );
          })}
        </div>
      </DragDropContext>

      <AddTaskModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTask}
        editingTask={editingTask}
        columns={boardData.boards[boardData.currentBoardId].columns}
        users={boardData.boards[boardData.currentBoardId].users}
      />
    </motion.div>
  );
};

export default Board;
